/**
 * Asset Detail drawer (PRD 10.2).
 *
 * Progressive disclosure: the headline score first, then the score components,
 * then the 500 m intelligence with provenance on every value, then the
 * alternatives and their trade-offs.
 */

import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { AssetIntelligence, AssetScoreOut, FeatureValue } from '../lib/types'
import { ASSET_CLASS_LABEL, money, moneyShort, provenanceLine, shortDate, titleCase } from '../lib/format'
import { Callout } from './Callout'
import { AvailabilityChip, Chip, FeatureChips } from './Chips'
import { FeatureMetric, Skeleton } from './Metric'
import { ScoreBreakdown } from './ScoreBreakdown'

function FeatureTable({ title, features }: { title: string; features: Record<string, FeatureValue> }) {
  const entries = Object.entries(features)
  if (!entries.length) return null
  const available = entries.filter(([, f]) => f.data_class !== 'unavailable')
  const missing = entries.filter(([, f]) => f.data_class === 'unavailable')
  return (
    <div className="stack tight">
      <h4>{title}</h4>
      <div className="table-wrap">
        <table>
          <tbody>
            {available.map(([key, feature]) => (
              <tr key={key}>
                <td className="small">{feature.label}</td>
                <td className="num tnum nowrap" title={provenanceLine(feature)}>
                  {formatFeature(feature)}
                </td>
                <td style={{ width: 1 }}>
                  <FeatureChips feature={feature} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {missing.length ? (
        <details className="disclosure">
          <summary>No data for {missing.length} of these</summary>
          <div className="stack tight">
            {missing.map(([key, feature]) => (
              <div key={key} className="tiny">
                <strong>{feature.label}</strong>
                <div className="faint">{feature.unavailable_reason}</div>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  )
}

function formatFeature(feature: FeatureValue): string {
  if (feature.value === null) return '—'
  switch (feature.unit) {
    case 'index_0_1':
    case 'share_0_1':
      return `${(feature.value * 100).toFixed(feature.value < 0.1 ? 1 : 0)}%`
    case 'minor_units_per_1000_target_impressions':
      return money(feature.value)
    case 'm':
      return feature.value >= 1000
        ? `${(feature.value / 1000).toFixed(1)} km`
        : `${Math.round(feature.value)} m`
    case 'kmph':
      return `${feature.value.toFixed(0)} km/h`
    case 's':
      return `${feature.value.toFixed(0)} s`
    case 'minutes':
      return `${feature.value.toFixed(1)} min`
    case 'exposures_per_unique_passer':
      return `${feature.value.toFixed(1)}×`
    case 'ratio':
      return feature.value.toFixed(2)
    default:
      return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(feature.value)
  }
}

export function AssetDetail({
  score,
  briefId,
  radius,
  alternatives,
  onClose,
}: {
  score: AssetScoreOut | null
  briefId: string
  radius: number
  alternatives: Record<string, unknown>[]
  onClose: () => void
}) {
  const intelligence = useQuery<AssetIntelligence>({
    queryKey: ['intelligence', score?.captura_asset_id, briefId, radius],
    queryFn: () => api.intelligence(score!.captura_asset_id, briefId, radius),
    enabled: Boolean(score),
  })

  if (!score) return null
  const asset = score.asset

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${asset.name} detail`}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgb(10 12 16 / 45%)',
        display: 'grid',
        justifyItems: 'end',
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: 'min(620px, 100vw)',
          height: '100%',
          borderRadius: 0,
          display: 'grid',
          overflow: 'auto',
          gridTemplateRows: 'auto minmax(0, 1fr)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-head">
          <div className="grow">
            <h2>{asset.name}</h2>
            <div className="tiny faint">
              {asset.media_type} · {ASSET_CLASS_LABEL[asset.asset_class]}
              {asset.media_code ? ` · ${asset.media_code}` : ''}
              {asset.locality ? ` · ${asset.locality}` : ''}
              {asset.city_name ? `, ${asset.city_name}` : ''}
            </div>
          </div>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="scroll-y" style={{ padding: 14 }}>
          <div className="stack">
            {score.contains_synthetic_data ? (
              <Callout tone="synthetic" title="Sample data">
                One or more figures on this asset come from a synthetic source and cannot be
                quoted or booked.
              </Callout>
            ) : null}

            <ScoreBreakdown score={score} />

            <hr className="hr" />

            <div className="grid three">
              <FeatureMetric
                label="Cost for the window"
                feature={
                  score.price?.total_cost_minor
                    ? ({
                      key: 'price',
                      label: 'Cost',
                      value: score.price.total_cost_minor,
                      unit: 'minor',
                      data_class: score.price.data_class,
                      freshness: 'fresh',
                      confidence: null,
                      provenance: {
                        provider_id: 'captura',
                        provider_name: `Captura (${score.price.basis ?? 'unknown basis'})`,
                        methodology_version: 'captura',
                        observed_at: score.price.quoted_at,
                        observed_from: null,
                        observed_to: null,
                        ingested_at: null,
                        coverage: 1,
                        geographic_grain: 'asset',
                        is_synthetic: score.price.data_class === 'synthetic',
                      },
                      value_json: null,
                      unavailable_reason: score.price.basis
                        ? `Derived from the ${score.price.basis.replace(/_/g, ' ')}`
                        : null,
                      snapshot_id: null,
                    } as FeatureValue)
                    : null
                }
              />
              <div className="metric">
                <div className="label">Availability</div>
                <div className="value" style={{ fontSize: 14 }}>
                  <AvailabilityChip
                    available={score.availability?.is_available}
                    bookedDays={score.availability?.booked_dates.length}
                  />
                </div>
                {score.availability ? (
                  <div className="note">
                    Checked {shortDate(score.availability.checked_at)} from{' '}
                    {score.availability.source.replace(/_/g, ' ')}
                  </div>
                ) : null}
              </div>
              <div className="metric">
                <div className="label">Survey score</div>
                <div className="value">
                  {asset.site_score !== null ? `${asset.site_score.toFixed(0)}/90` : '—'}
                </div>
                <div className="note">
                  {asset.site_score_updated_at
                    ? `surveyed ${shortDate(asset.site_score_updated_at)}`
                    : 'never surveyed'}
                </div>
              </div>
            </div>

            {score.availability?.conflicts.length ? (
              <Callout tone="warn" title="Occupied during the campaign window">
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {score.availability.conflicts.map((conflict, i) => (
                    <li key={i} className="small">
                      {String(conflict.campaign_name ?? conflict.kind)}
                      {conflict.campaign_state ? ` (${conflict.campaign_state})` : ''}
                      {conflict.start_date ? `: ${conflict.start_date} → ${conflict.end_date}` : ''}
                    </li>
                  ))}
                </ul>
              </Callout>
            ) : null}

            <hr className="hr" />

            <div>
              <h3>{radius} m intelligence</h3>
              <div className="tiny faint">
                Every value carries its source, freshness and confidence. Hover for provenance.
              </div>
            </div>

            {intelligence.isLoading ? (
              <Skeleton rows={6} />
            ) : intelligence.isError ? (
              <Callout tone="warn" title="Could not load the intelligence profile">
                The score above is unaffected; it was computed during the run.
              </Callout>
            ) : intelligence.data ? (
              <>
                <FeatureTable
                  title={`${ASSET_CLASS_LABEL[asset.asset_class]} KPI profile`}
                  features={intelligence.data.kpi_profile.kpis}
                />
                <FeatureTable title="Visibility (Captura survey)" features={intelligence.data.visibility} />
                <FeatureTable title="Audience" features={intelligence.data.audience} />
                <FeatureTable title="Mobility" features={intelligence.data.mobility} />

                {Object.keys(intelligence.data.poi_breakdown).length ? (
                  <div className="stack tight">
                    <h4>Points of interest in the buffer</h4>
                    <div className="pill-row">
                      {Object.entries(intelligence.data.poi_breakdown)
                        .sort((a, b) => b[1] - a[1])
                        .map(([category, count]) => (
                          <Chip key={category}>
                            {titleCase(category)} {count}
                          </Chip>
                        ))}
                    </div>
                    <details className="disclosure">
                      <summary>Nearest {intelligence.data.nearest_pois.length}</summary>
                      <div className="table-wrap">
                        <table>
                          <tbody>
                            {intelligence.data.nearest_pois.map((poi, i) => (
                              <tr key={i}>
                                <td className="small">{poi.name}</td>
                                <td className="small muted">{titleCase(poi.category)}</td>
                                <td className="num tnum">{Math.round(poi.distance_m)} m</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </div>
                ) : (
                  <Callout tone="info" title="No point-of-interest data in range">
                    POI relevance is reported as unavailable rather than estimated.
                  </Callout>
                )}
              </>
            ) : null}

            {alternatives.length ? (
              <>
                <hr className="hr" />
                <div className="stack tight">
                  <h3>Alternatives</h3>
                  <div className="tiny faint">
                    The closest substitutes that would still fit the budget, and what you would
                    trade.
                  </div>
                  {alternatives.map((alt, i) => (
                    <div key={i} className="callout info">
                      <span className="icon" aria-hidden>
                        ⇄
                      </span>
                      <div className="body">
                        <strong>{String(alt.name)}</strong>
                        {alt.locality ? <span className="faint"> · {String(alt.locality)}</span> : null}
                        <div className="small">{String(alt.trade_off)}</div>
                        <div className="tiny faint">
                          Suitability {Number(alt.total_score).toFixed(1)} ·{' '}
                          {moneyShort(Number(alt.cost_minor))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            <details className="disclosure">
              <summary>Asset record</summary>
              <dl className="kv">
                <dt>Captura id</dt>
                <dd className="mono tiny">{asset.captura_asset_id}</dd>
                <dt>Address</dt>
                <dd>{asset.address ?? '—'}</dd>
                <dt>Landmark</dt>
                <dd>{asset.landmark ?? '—'}</dd>
                <dt>Size</dt>
                <dd>
                  {asset.width_ft && asset.height_ft
                    ? `${asset.width_ft} × ${asset.height_ft} ft (${asset.area_sqft?.toFixed(0)} sq ft)`
                    : '—'}
                </dd>
                <dt>Medium</dt>
                <dd>{asset.medium_type ?? '—'}</dd>
                <dt>Strategy tier</dt>
                <dd>{asset.strategy_tier ?? '—'}</dd>
                <dt>Partner</dt>
                <dd>{asset.partner_name ?? '—'}</dd>
                <dt>Coordinates</dt>
                <dd className="mono tiny">
                  {asset.latitude?.toFixed(5)}, {asset.longitude?.toFixed(5)}
                </dd>
                {asset.loop_duration_min ? (
                  <>
                    <dt>Loop</dt>
                    <dd>
                      {asset.loop_duration_min} min, {asset.slot_duration_sec} s slot,{' '}
                      {asset.total_slots} slots
                    </dd>
                  </>
                ) : null}
              </dl>
            </details>
          </div>
        </div>
      </div>
    </div>
  )
}
